import { CURRENCY } from "../constants";
import { activeLocale } from "./date-format-utils";

/**
 * The platform's single money formatter.
 *
 * MED-1543 — amounts reached the screen three different ways, and none of them
 * agreed with the others:
 *
 *  - `CURRENCY_SYMBOL` ("Fcfa ") concatenated onto `value.toLocaleString()`,
 *    which takes the *browser's* locale, so a French UI in an en-US browser
 *    rendered "Fcfa 20,000" — a US thousands separator and a symbol spelt the
 *    way no CFA convention spells it;
 *  - `priceFormatter`, which hardcoded "en-US" and so rendered
 *    "F CFA 770,000" in French too;
 *  - the design system's `formatCurrency`, which is Intl + XOF but likewise
 *    reads the browser locale rather than the UI language.
 *
 * Going through `activeLocale()` is what fixes all three: the locale is the
 * language react-i18next is currently serving, read at call time so the
 * rendered format follows a language switch, exactly as the date formatters in
 * {@link ./date-format-utils} do. CLDR then supplies the grouping *and* the
 * symbol, so French reads "770 000 F CFA" and English "F CFA 770,000" and the
 * spelling is consistent wherever an amount appears.
 *
 * XOF carries no minor unit in CLDR, so amounts render without decimals; the
 * API sends whole francs.
 */
export function formatMoney(
  value: number,
  locale: string = activeLocale()
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: CURRENCY,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Same as {@link formatMoney}, but returns `undefined` for an amount the API
 * did not send, so a caller can drop the row rather than claim the amount is
 * zero (MED-1532 made this distinction on the licence detail panel: a missing
 * monthly amount must show "—", not "F CFA 0").
 */
export function formatMoneyOrNull(
  value: number | null | undefined,
  locale: string = activeLocale()
): string | undefined {
  return value == null ? undefined : formatMoney(value, locale);
}
